import { collection, addDoc, query, where, orderBy, getDocs, serverTimestamp, Timestamp, limit } from 'firebase/firestore';
import { db } from '@/firebase/config';

// Unified audit action types - includes both user actions and compliance/location actions
export type AuditAction = 
  // User actions
  | 'user_created'
  | 'user_updated'
  | 'user_removed'
  | 'location_assigned'
  | 'location_removed'
  | 'role_changed'
  | 'permission_changed'
  | 'user_login'
  | 'user_logout'
  | 'invitation_sent'
  | 'invitation_accepted'
  | 'invitation_cancelled'
  // Compliance actions
  | 'compliance_added'
  | 'compliance_updated'
  | 'compliance_deleted'
  | 'compliance_assigned'
  | 'compliance_removed'
  // Location actions
  | 'location_added'
  | 'location_updated'
  | 'location_deleted'
  // Floor actions
  | 'floor_added'
  | 'floor_updated'
  | 'floor_deleted'
  // Form actions
  | 'form_added'
  | 'form_updated'
  | 'form_deleted'
  | 'form_compliance_changed'
  | 'form_location_changed'
  // Checklist actions
  | 'checklist_added'
  | 'checklist_updated'
  | 'checklist_deleted'
  | 'checklist_compliance_changed'
  | 'checklist_location_changed'
  // Asset actions
  | 'asset_added'
  | 'asset_updated'
  | 'asset_deleted'
  | 'asset_compliance_changed'
  | 'asset_location_changed'
  // Audit template actions
  | 'audit_template_added'
  | 'audit_template_updated'
  | 'audit_template_deleted'
  // Audit instance actions
  | 'audit_created'
  | 'audit_updated'
  | 'audit_status_updated'
  | 'audit_deleted'
  // Admin preview actions
  | 'preview_mode_entered'
  | 'preview_mode_exited';

// Unified audit log entry interface that supports both formats
export interface AuditLogEntry {
  id?: string;
  action: AuditAction;
  organizationId?: string;
  timestamp: Timestamp | Date | any;
  
  // Legacy format fields (for user actions)
  userId?: string; // The user being acted upon
  performedBy?: string; // User ID of who performed the action
  performedByName?: string; // Name of who performed the action
  performedByEmail?: string; // Email of who performed the action
  changes?: {
    field: string;
    oldValue: any;
    newValue: any;
  }[];
  metadata?: Record<string, any>; // Additional context (e.g., location names, role names)
  ipAddress?: string;
  userAgent?: string;
  
  // New format fields (for compliance/location actions)
  entityType?: 'compliance' | 'location' | 'floor' | 'compliance_assignment' | 'user' | 'form' | 'checklist' | 'asset' | 'audit_template' | 'audit';
  entityId?: string;
  entityName?: string;
  userName?: string; // Name of who performed the action (new format)
  userEmail?: string; // Email of who performed the action (new format)
  details?: {
    oldValue?: any;
    newValue?: any;
    locationId?: string;
    locationName?: string;
    locationIds?: string[]; // For checklists with multiple locations
    oldLocationIds?: string[];
    newLocationIds?: string[];
    complianceId?: string;
    complianceName?: string;
    oldComplianceId?: string;
    newComplianceId?: string;
    oldComplianceName?: string;
    newComplianceName?: string;
    deletedTasksCount?: number; // Number of tasks deleted when a location is deleted
  };
}

export const auditLogService = {
  // Create an audit log entry (unified method - supports both formats)
  log: async (entry: Omit<AuditLogEntry, 'id' | 'timestamp'>): Promise<{ success: boolean; id?: string; error?: string }> => {
    try {
      const auditRef = collection(db, 'audit_logs');
      const logData = {
        ...entry,
        timestamp: serverTimestamp(),
      };
      
      const docRef = await addDoc(auditRef, logData);
      return { success: true, id: docRef.id };
    } catch (error: any) {
      console.error('Error creating audit log:', error);
      // Don't throw - audit logging should not break the main operation
      return { success: false, error: error.message };
    }
  },

  // Alias for backward compatibility (new format uses createLog)
  createLog: async (entry: Omit<AuditLogEntry, 'id' | 'timestamp'>): Promise<{ success: boolean; id?: string; error?: string }> => {
    return auditLogService.log(entry);
  },

  // Get audit logs for a specific user
  getUserAuditLogs: async (userId: string, organizationId?: string, limit: number = 100): Promise<{ success: boolean; data?: AuditLogEntry[]; error?: string }> => {
    try {
      const auditRef = collection(db, 'audit_logs');
      
      // First, try to get logs filtered by organizationId if provided
      // If that returns nothing, also try without organizationId filter (for logs that might not have organizationId set)
      let q = query(
        auditRef,
        where('userId', '==', userId),
        orderBy('timestamp', 'desc')
      );

      // Filter by organization if provided
      if (organizationId) {
        q = query(
          auditRef,
          where('userId', '==', userId),
          where('organizationId', '==', organizationId),
          orderBy('timestamp', 'desc')
        );
      }

      const querySnapshot = await getDocs(q);
      const logs: AuditLogEntry[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        logs.push({
          id: doc.id,
          ...data,
          timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : (data.timestamp || new Date()),
        } as AuditLogEntry);
      });

      // If we filtered by organizationId and got no results, also try without the filter
      // This handles cases where audit logs might have been created without organizationId
      if (organizationId && logs.length === 0) {
        console.log('⚠️ No logs found with organizationId filter, trying without filter...');
        const qWithoutOrg = query(
          auditRef,
          where('userId', '==', userId),
          orderBy('timestamp', 'desc')
        );
        const querySnapshotWithoutOrg = await getDocs(qWithoutOrg);
        querySnapshotWithoutOrg.forEach((doc) => {
          const data = doc.data();
          // Only include logs that either don't have organizationId or match the current one
          if (!data.organizationId || data.organizationId === organizationId) {
            logs.push({
              id: doc.id,
              ...data,
              timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : (data.timestamp || new Date()),
            } as AuditLogEntry);
          }
        });
      }

      // Limit results
      const limited = logs.slice(0, limit);

      return { success: true, data: limited };
    } catch (error: any) {
      console.error('Error getting user audit logs:', error);
      
      // Check if it's an index error
      if (error.message && error.message.includes('index')) {
        console.error('⚠️ Firestore index required. Please deploy indexes using: firebase deploy --only firestore:indexes');
        console.error('Or create the index manually in Firebase Console > Firestore > Indexes');
      }
      
      return { success: false, error: error.message };
    }
  },

  // Get audit logs for actions performed by a specific user
  getActionsByUser: async (performedBy: string, organizationId?: string, limitCount: number = 100): Promise<{ success: boolean; data?: AuditLogEntry[]; error?: string }> => {
    try {
      const auditRef = collection(db, 'audit_logs');
      let q = query(
        auditRef,
        where('performedBy', '==', performedBy),
        orderBy('timestamp', 'desc')
      );

      // Filter by organization if provided
      if (organizationId) {
        q = query(
          auditRef,
          where('performedBy', '==', performedBy),
          where('organizationId', '==', organizationId),
          orderBy('timestamp', 'desc')
        );
      }

      const querySnapshot = await getDocs(q);
      const logs: AuditLogEntry[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        logs.push({
          id: doc.id,
          ...data,
          timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : (data.timestamp || new Date()),
        } as AuditLogEntry);
      });

      // Limit results
      const limited = logs.slice(0, limitCount);

      return { success: true, data: limited };
    } catch (error: any) {
      console.error('Error getting actions by user:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all audit logs for an organization (unified method)
  getLogs: async (organizationId: string, limitCount: number = 200): Promise<{ success: boolean; data?: AuditLogEntry[]; error?: string }> => {
    try {
      const auditRef = collection(db, 'audit_logs');
      const q = query(
        auditRef,
        where('organizationId', '==', organizationId),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(q);
      const logs: AuditLogEntry[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        logs.push({
          id: doc.id,
          ...data,
          timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : (data.timestamp || new Date()),
        } as AuditLogEntry);
      });
      
      return { success: true, data: logs };
    } catch (error: any) {
      console.error('Error getting audit logs:', error);
      // Try without orderBy if index error
      if (error.message && error.message.includes('index')) {
        try {
          const auditRef = collection(db, 'audit_logs');
          const q = query(
            auditRef,
            where('organizationId', '==', organizationId),
            limit(limitCount)
          );
          const snapshot = await getDocs(q);
          const logs: AuditLogEntry[] = [];
          snapshot.forEach((doc) => {
            const data = doc.data();
            logs.push({
              id: doc.id,
              ...data,
              timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : (data.timestamp || new Date()),
            } as AuditLogEntry);
          });
          // Sort in memory
          logs.sort((a, b) => {
            const aTime = a.timestamp instanceof Date ? a.timestamp.getTime() : (a.timestamp?.toDate ? a.timestamp.toDate().getTime() : 0);
            const bTime = b.timestamp instanceof Date ? b.timestamp.getTime() : (b.timestamp?.toDate ? b.timestamp.toDate().getTime() : 0);
            return bTime - aTime;
          });
          return { success: true, data: logs };
        } catch (fallbackError: any) {
          return { success: false, error: fallbackError.message, data: [] };
        }
      }
      return { success: false, error: error.message, data: [] };
    }
  },

  // Get audit logs filtered by entity type
  getLogsByEntityType: async (
    organizationId: string, 
    entityType: 'compliance' | 'location' | 'compliance_assignment' | 'form' | 'checklist' | 'asset',
    limitCount: number = 200
  ): Promise<{ success: boolean; data?: AuditLogEntry[]; error?: string }> => {
    try {
      const auditRef = collection(db, 'audit_logs');
      const q = query(
        auditRef,
        where('organizationId', '==', organizationId),
        where('entityType', '==', entityType),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(q);
      const logs: AuditLogEntry[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        logs.push({
          id: doc.id,
          ...data,
          timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : (data.timestamp || new Date()),
        } as AuditLogEntry);
      });
      
      return { success: true, data: logs };
    } catch (error: any) {
      console.error('Error getting audit logs by entity type:', error);
      return { success: false, error: error.message, data: [] };
    }
  },
};

